import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { MagnifyingGlass, Plus, Buildings, ArrowRight, Eye, Pencil, Globe, EnvelopeSimple, Phone, MapPin, Hash, Briefcase, UsersThree, Notepad } from "@phosphor-icons/react";
import { toast } from "sonner";
import CallButton, { PhoneList } from "@/components/CallButton";
import Pagination from "@/components/Pagination";
import CreatorSelect from "@/components/CreatorSelect";

const EMPTY_FORM = { created_by: "", name: "", reg_no: "", address: "", sic_code: "", domain: "", email: "", phone: "", phone_direct: "", phone_mobile: "", phone_hr: "", industry: "", company_size: "", website: "", notes: "", directors: [] };

export default function CompaniesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [externalResults, setExternalResults] = useState([]);
  const [extQuery, setExtQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get(`/companies?search=${search}&page=${page}&limit=25`);
      setCompanies(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const openAddForm = () => { setEditingCompany(null); setForm({ ...EMPTY_FORM }); setShowForm(true); };

  const openEditForm = (c) => {
    setEditingCompany(c);
    setForm({
      name: c.name || "", reg_no: c.reg_no || "", address: c.address || "",
      sic_code: c.sic_code || "", domain: c.domain || "", email: c.email || "",
      phone: c.phone || "", phone_direct: c.phone_direct || "", phone_mobile: c.phone_mobile || "", phone_hr: c.phone_hr || "",
      industry: c.industry || "", company_size: c.company_size || "",
      website: c.website || "", notes: c.notes || "", directors: c.directors || [],
    });
    setShowDetail(false);
    setShowForm(true);
  };

  const openDetail = async (c) => {
    try { const { data } = await api.get(`/companies/${c.id}`); setSelectedCompany(data); }
    catch { setSelectedCompany(c); }
    setShowDetail(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCompany) {
        await api.put(`/companies/${editingCompany.id}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/companies", form);
        toast.success("Company added");
      }
      setShowForm(false);
      setForm({ ...EMPTY_FORM });
      setEditingCompany(null);
      loadCompanies();
    } catch { toast.error("Failed to save company"); }
    finally { setSaving(false); }
  };

  const handleExternalSearch = async () => {
    try {
      const { data } = await api.get(`/external/companies-house/search?q=${extQuery}`);
      setExternalResults(data.items || []);
    } catch { toast.error("Search failed"); }
  };

  const importCompany = async (c) => {
    try { await api.post("/companies", c); toast.success(`${c.name} imported`); loadCompanies(); }
    catch { toast.error("Import failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this company?")) return;
    try { await api.delete(`/companies/${id}`); toast.success("Deleted"); setShowDetail(false); loadCompanies(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const f = (v) => v || "-";

  return (
    <div data-testid="companies-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Companies</h1>
          <p className="text-sm text-[#6B7280]">{total} companies in your database</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSearch(true)} className="gap-2 border-[#E5E7EB]" data-testid="search-companies-house-btn">
            <MagnifyingGlass size={16} /> Companies House
          </Button>
          <Button onClick={openAddForm} className="gap-2 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="add-company-btn">
            <Plus size={16} /> Add Company
          </Button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input placeholder="Search companies..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 border-[#E5E7EB]" data-testid="company-search-input" />
      </div>

      <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bm-table-header">
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Reg No</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Domain</TableHead>
                <TableHead className="text-xs hidden md:table-cell">Industry</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Created By</TableHead>
                <TableHead className="text-xs hidden lg:table-cell">Phone</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => <TableRow key={i}><TableCell colSpan={6}><div className="h-8 skeleton-shimmer rounded" /></TableCell></TableRow>)
              ) : companies.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#6B7280]">No companies found. Add or import your first company.</TableCell></TableRow>
              ) : companies.map((c) => (
                <TableRow key={c.id} className="hover:bg-[#F9FAFB] transition-colors cursor-pointer" onClick={() => openDetail(c)} data-testid={`company-row-${c.id}`}>
                  <TableCell className="font-medium text-[#0F172A]">{c.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-[#6B7280] text-sm">{c.reg_no || "-"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-[#6B7280] text-sm">{c.domain || "-"}</TableCell>
                  <TableCell className="hidden md:table-cell text-[#6B7280] text-sm">{c.industry || "-"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-[#9CA3AF] text-xs">{c.created_by_name || "-"}</TableCell>
                  <TableCell className="hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                    {c.phone ? <CallButton phone={c.phone} compact showNumber /> : <span className="text-[#6B7280] text-sm">-</span>}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openDetail(c)} className="text-[#0F172A] hover:bg-[#F3F4F6] text-xs" data-testid={`view-company-${c.id}`}>
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination page={page} total={total} limit={25} onPageChange={(p) => { setPage(p); setLoading(true); }} />
        </div>
      </div>

      {/* ==================== DETAIL DIALOG ==================== */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] text-lg">{selectedCompany?.name}</DialogTitle>
          </DialogHeader>
          {selectedCompany && (
            <div className="space-y-5 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailField icon={Hash} label="Reg No" value={f(selectedCompany.reg_no)} />
                <DetailField icon={Briefcase} label="SIC Code" value={f(selectedCompany.sic_code)} />
                <DetailField icon={MapPin} label="Address" value={f(selectedCompany.address)} />
                <DetailField icon={Globe} label="Domain" value={f(selectedCompany.domain)} />
                <DetailField icon={EnvelopeSimple} label="Email" value={f(selectedCompany.email)} />
                <DetailField icon={Buildings} label="Industry" value={f(selectedCompany.industry)} />
                <DetailField icon={UsersThree} label="Company Size" value={f(selectedCompany.company_size)} />
                <DetailField icon={Globe} label="Website" value={f(selectedCompany.website)} />
              </div>

              {/* Phone Numbers with Call Buttons */}
              {(selectedCompany.phone || selectedCompany.phone_direct || selectedCompany.phone_mobile || selectedCompany.phone_hr) && (
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Phone Numbers</p>
                  <PhoneList phones={[
                    { label: "Main Office", number: selectedCompany.phone },
                    { label: "Direct Line", number: selectedCompany.phone_direct },
                    { label: "Mobile", number: selectedCompany.phone_mobile },
                    { label: "Recruitment/HR", number: selectedCompany.phone_hr },
                  ]} />
                </div>
              )}

              {selectedCompany.directors && selectedCompany.directors.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Directors</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCompany.directors.map((d, i) => <span key={i} className="px-2.5 py-1 bg-[#F3F4F6] rounded text-sm text-[#374151]">{d}</span>)}
                  </div>
                </div>
              )}
              {selectedCompany.notes && (
                <div>
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Notes</p>
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 text-sm text-[#374151] whitespace-pre-wrap">{selectedCompany.notes}</div>
                </div>
              )}
              <div className="text-xs text-[#9CA3AF]">Created: {selectedCompany.created_at ? new Date(selectedCompany.created_at).toLocaleString() : "-"}</div>
              <Separator />
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => openEditForm(selectedCompany)} className="gap-2 bg-[#0F172A] hover:bg-[#1E293B]" data-testid="edit-company-btn">
                  <Pencil size={14} /> Edit Company
                </Button>
                {isAdmin && (
                  <Button variant="outline" onClick={() => handleDelete(selectedCompany.id)} className="gap-1 border-[#EF4444] text-[#EF4444] hover:bg-red-50 ml-auto" data-testid="detail-delete-company-btn">Delete</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== ADD / EDIT DIALOG ==================== */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#0F172A]">{editingCompany ? "Edit Company" : "Add Company"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-[#C5A059] uppercase tracking-wide mb-2">Company Info</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label className="text-xs">Company Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-10 border-[#E5E7EB]" data-testid="company-name-input" /></div>
                <div><Label className="text-xs">Reg No</Label><Input value={form.reg_no} onChange={(e) => setForm({ ...form, reg_no: e.target.value })} className="h-10 border-[#E5E7EB]" /></div>
                <div><Label className="text-xs">SIC Code</Label><Input value={form.sic_code} onChange={(e) => setForm({ ...form, sic_code: e.target.value })} className="h-10 border-[#E5E7EB]" /></div>
                <div><Label className="text-xs">Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Financial Services" className="h-10 border-[#E5E7EB]" data-testid="company-industry-input" /></div>
                <div><Label className="text-xs">Company Size</Label><Input value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })} placeholder="e.g. 500-1000" className="h-10 border-[#E5E7EB]" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-10 border-[#E5E7EB]" /></div>
                {!editingCompany && (
                  <div className="sm:col-span-2">
                    <CreatorSelect value={form.created_by} onChange={(v) => setForm({ ...form, created_by: v })} testId="company-creator-select" />
                  </div>
                )}
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-xs font-semibold text-[#C5A059] uppercase tracking-wide mb-2">Contact & Web</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Domain</Label><Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="h-10 border-[#E5E7EB]" /></div>
                <div><Label className="text-xs">Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className="h-10 border-[#E5E7EB]" /></div>
                <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-10 border-[#E5E7EB]" /></div>
                <div><Label className="text-xs">Main Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44..." className="h-10 border-[#E5E7EB]" data-testid="company-phone-input" /></div>
                <div><Label className="text-xs">Direct Line</Label><Input value={form.phone_direct} onChange={(e) => setForm({ ...form, phone_direct: e.target.value })} placeholder="+44..." className="h-10 border-[#E5E7EB]" data-testid="company-phone-direct-input" /></div>
                <div><Label className="text-xs">Mobile</Label><Input value={form.phone_mobile} onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })} placeholder="+44..." className="h-10 border-[#E5E7EB]" data-testid="company-phone-mobile-input" /></div>
                <div><Label className="text-xs">Recruitment/HR</Label><Input value={form.phone_hr} onChange={(e) => setForm({ ...form, phone_hr: e.target.value })} placeholder="+44..." className="h-10 border-[#E5E7EB]" data-testid="company-phone-hr-input" /></div>
              </div>
            </div>
            <Separator />
            <div>
              <Label className="text-xs">Notes (internal)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Internal notes about this company..." className="border-[#E5E7EB] bg-yellow-50" data-testid="company-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-[#E5E7EB]">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0F172A] hover:bg-[#1E293B]" data-testid="submit-company-btn">{saving ? "Saving..." : editingCompany ? "Update Company" : "Add Company"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== COMPANIES HOUSE SEARCH ==================== */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-[#0F172A] flex items-center gap-2"><Buildings size={20} weight="duotone" className="text-[#1E3A8A]" /> Companies House Search</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="Search UK companies..." value={extQuery} onChange={(e) => setExtQuery(e.target.value)} className="h-10 border-[#E5E7EB]" data-testid="ext-company-search-input" />
            <Button onClick={handleExternalSearch} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90" data-testid="ext-company-search-btn">Search</Button>
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-2 mt-2">
            {externalResults.map((c, i) => (
              <div key={i} className="p-3 border border-[#E5E7EB] rounded-lg hover:border-[#C5A059] transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-sm text-[#0F172A]">{c.name}</p>
                    <p className="text-xs text-[#6B7280] mt-0.5">{c.address}</p>
                    <p className="text-xs text-[#6B7280]">Reg: {c.reg_no} | SIC: {c.sic_code}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => importCompany(c)} className="text-xs gap-1 border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10" data-testid={`import-company-${i}`}>
                    <ArrowRight size={12} /> Import
                  </Button>
                </div>
              </div>
            ))}
            {externalResults.length === 0 && extQuery && <p className="text-sm text-[#6B7280] text-center py-4">Search for companies to see results</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5 p-2.5 bg-[#F9FAFB] rounded-lg">
      <div className="w-8 h-8 rounded-md bg-white border border-[#E5E7EB] flex items-center justify-center flex-shrink-0">
        <Icon size={16} weight="duotone" className="text-[#6B7280]" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide font-medium">{label}</p>
        <p className="text-sm text-[#0F172A] font-medium truncate">{value}</p>
      </div>
    </div>
  );
}
